import { useState, useRef } from "react";
import { UploadCloud, FileText, X, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

export default function ResumeDropzone({ onUpload, isUploading }) {
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const addFiles = (fileList) => {
    const valid = Array.from(fileList).filter((f) => /\.(pdf|docx)$/i.test(f.name));
    setFiles((prev) => {
      const names = new Set(prev.map((f) => f.name));
      return [...prev, ...valid.filter((f) => !names.has(f.name))];
    });
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const handleSubmit = () => {
    if (files.length === 0) return;
    onUpload(files);
    setFiles([]);
  };

  return (
    <div>
      <div
        data-testid="resume-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          addFiles(e.dataTransfer.files);
        }}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-10 text-center transition-colors duration-200 ${
          dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
        }`}
      >
        <UploadCloud className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-3 text-sm text-foreground">Click to upload or drag and drop resumes</p>
        <p className="text-xs text-muted-foreground mt-1">PDF and DOCX — bulk upload supported</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.docx"
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
          data-testid="resume-file-input"
        />
      </div>

      {files.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="resume-file-list">
          {files.map((f, idx) => (
            <div key={idx} className="flex items-center justify-between rounded-lg bg-muted px-3 py-2 text-sm">
              <span className="flex items-center gap-2 truncate">
                <FileText className="h-4 w-4 shrink-0" />
                {f.name}
              </span>
              <button onClick={() => removeFile(idx)} data-testid={`resume-remove-btn-${idx}`}>
                <X className="h-4 w-4 text-muted-foreground hover:text-destructive transition-colors" />
              </button>
            </div>
          ))}
          <Button onClick={handleSubmit} disabled={isUploading} data-testid="evaluate-candidates-btn" className="mt-2 w-full">
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Evaluating {files.length} resume(s)...
              </>
            ) : (
              `Evaluate ${files.length} Candidate(s)`
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
